import type {
  ExpressionSpecification,
  FillLayerSpecification,
  FilterSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
  VectorSourceSpecification,
} from 'maplibre-gl';

import { BARRIER_ICON_IMAGE_ID } from './barrierIcon';
import { GREEN_FLAG_ICON_IMAGE_ID } from './greenFlagIcon';

export const BARRIERS_SOURCE_ID = 'barriers';
export const BARRIERS_SOURCE_LAYER = 'barriers';
export const BARRIERS_ZONE_LAYER_ID = 'barriers-zones';
export const BARRIERS_ICON_LAYER_ID = 'barriers-icons';
export const BARRIERS_WAYPOINT_LAYER_ID = 'barriers-waypoint-flags';
export const BARRIERS_ZONE_SELECTED_LAYER_ID = 'barriers-zones-selected';
export const BARRIERS_ICON_SELECTED_LAYER_ID = 'barriers-icons-selected';
export const BARRIERS_ZONE_RESIDUAL_LAYER_ID = 'barriers-zones-residual';
export const BARRIERS_ICON_HOVERED_LAYER_ID = 'barriers-icons-hovered';
export const BARRIERS_ZONE_HOVERED_LAYER_ID = 'barriers-zones-hovered';

const ZONE_FILL_COLOR = '#cf1322';
const ZONE_RESIDUAL_STROKE_COLOR = '#cf1322';
const ZONE_RESIDUAL_STROKE_WIDTH: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 2,
  10, 3,
  14, 4,
];
const ZONE_FILL_OPACITY: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 0.08,
  12, 0.18,
  16, 0.28,
];
const ZONE_SELECTED_FILL_OPACITY = 0.4;

const ICON_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 0.5,
  10, 0.7,
  14, 0.9,
];

const FILTER_ZONE: FilterSpecification = ['==', ['get', 'feature_type'], 'zone'];
const FILTER_ICON: FilterSpecification = ['==', ['get', 'feature_type'], 'icon'];
const FILTER_WAYPOINT: FilterSpecification = [
  '==', ['get', 'feature_type'], 'waypoint',
];

// Detour flags only appear once the basemap shows streets, so they read
// as "turn here" rather than cluttering the regional view.
const WAYPOINT_FLAG_MIN_ZOOM = 14;
const WAYPOINT_FLAG_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  14, 0.75,
  16, 1.0,
  19, 1.2,
];

export function buildBarriersSource(
  apiBaseUrl: string,
  revision: number,
): VectorSourceSpecification {
  return {
    type: 'vector',
    tiles: [buildTilesUrl(apiBaseUrl, revision)],
    minzoom: 0,
    maxzoom: 22,
  };
}

export function buildTilesUrl(apiBaseUrl: string, revision: number): string {
  return `${apiBaseUrl}/api/v1/tiles/barriers/{z}/{x}/{y}.mvt?v=${revision}`;
}

export function buildZoneFillLayer(): FillLayerSpecification {
  return {
    id: BARRIERS_ZONE_LAYER_ID,
    type: 'fill',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: FILTER_ZONE,
    paint: {
      'fill-color': ZONE_FILL_COLOR,
      'fill-opacity': ZONE_FILL_OPACITY,
      'fill-antialias': true,
    },
  };
}

export function buildZoneSelectedLayer(selectedId: number): FillLayerSpecification {
  return {
    id: BARRIERS_ZONE_SELECTED_LAYER_ID,
    type: 'fill',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: buildSelectedFilter('zone', selectedId),
    paint: {
      'fill-color': ZONE_FILL_COLOR,
      'fill-opacity': ZONE_SELECTED_FILL_OPACITY,
    },
  };
}

export function buildIconSymbolLayer(): SymbolLayerSpecification {
  return {
    id: BARRIERS_ICON_LAYER_ID,
    type: 'symbol',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: FILTER_ICON,
    layout: {
      'icon-image': BARRIER_ICON_IMAGE_ID,
      'icon-size': ICON_SIZE,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  };
}

/**
 * Green-flag markers for every barrier's `auto_waypoints`. Rendered from
 * the same vector source (`feature_type='waypoint'`) but only at street
 * zoom (`WAYPOINT_FLAG_MIN_ZOOM`+), and anchored at the pole base so the
 * flag plants exactly on the detour coordinate. Non-interactive — purely
 * a visual cue for where a barrier funnels traffic.
 */
export function buildWaypointFlagLayer(): SymbolLayerSpecification {
  return {
    id: BARRIERS_WAYPOINT_LAYER_ID,
    type: 'symbol',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    minzoom: WAYPOINT_FLAG_MIN_ZOOM,
    filter: FILTER_WAYPOINT,
    layout: {
      'icon-image': GREEN_FLAG_ICON_IMAGE_ID,
      'icon-size': WAYPOINT_FLAG_SIZE,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  };
}

export function buildIconSelectedLayer(selectedId: number): SymbolLayerSpecification {
  return {
    id: BARRIERS_ICON_SELECTED_LAYER_ID,
    type: 'symbol',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: buildSelectedFilter('icon', selectedId),
    layout: {
      'icon-image': BARRIER_ICON_IMAGE_ID,
      'icon-size': [
        'interpolate', ['linear'], ['zoom'],
        6, 0.7,
        10, 1.0,
        14, 1.3,
      ],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  };
}

export function buildSelectedFilter(
  featureType: 'icon' | 'zone',
  selectedId: number,
): FilterSpecification {
  return [
    'all',
    ['==', ['get', 'feature_type'], featureType],
    ['==', ['get', 'id'], selectedId],
  ];
}

export function buildEmptyFilter(featureType: 'icon' | 'zone'): FilterSpecification {
  return [
    'all',
    ['==', ['get', 'feature_type'], featureType],
    ['==', ['get', 'id'], -1],
  ];
}

export function buildZoneResidualLayer(): LineLayerSpecification {
  return {
    id: BARRIERS_ZONE_RESIDUAL_LAYER_ID,
    type: 'line',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: buildEmptyResidualFilter(),
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': ZONE_RESIDUAL_STROKE_COLOR,
      'line-width': ZONE_RESIDUAL_STROKE_WIDTH,
      'line-opacity': 0.95,
    },
  };
}

export function buildResidualFilter(ids: readonly number[]): FilterSpecification {
  if (ids.length === 0) {
    return buildEmptyResidualFilter();
  }
  return [
    'all',
    ['==', ['get', 'feature_type'], 'zone'],
    ['in', ['get', 'id'], ['literal', [...ids]]],
  ];
}

export function buildEmptyResidualFilter(): FilterSpecification {
  return [
    'all',
    ['==', ['get', 'feature_type'], 'zone'],
    ['==', ['get', 'id'], -1],
  ];
}

/**
 * Slightly bigger barrier icon rendered on top of the regular icon
 * layer when the user hovers it. The filter is updated by `MainMap`
 * in response to MapLibre `mouseenter`/`mouseleave` events.
 */
export function buildIconHoveredLayer(): SymbolLayerSpecification {
  return {
    id: BARRIERS_ICON_HOVERED_LAYER_ID,
    type: 'symbol',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: buildEmptyFilter('icon'),
    layout: {
      'icon-image': BARRIER_ICON_IMAGE_ID,
      'icon-size': [
        'interpolate', ['linear'], ['zoom'],
        6, 0.6,
        10, 0.85,
        14, 1.1,
      ],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  };
}

/**
 * Pulse-style outline of the hovered barrier zone. Same fill colour
 * as the base layer but with a higher opacity so it reads as a
 * highlight without a new colour token.
 */
export function buildZoneHoveredLayer(): FillLayerSpecification {
  return {
    id: BARRIERS_ZONE_HOVERED_LAYER_ID,
    type: 'fill',
    source: BARRIERS_SOURCE_ID,
    'source-layer': BARRIERS_SOURCE_LAYER,
    filter: buildEmptyFilter('zone'),
    paint: {
      'fill-color': ZONE_FILL_COLOR,
      'fill-opacity': 0.32,
    },
  };
}
